import LangEnum from '../../../lang/LangEnum';
import { ErrorResponse, TeceoApiResponse } from '../../../lib/basicRepository';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import { HTTP_STATUS } from '../../../utils/httpStatus.const';
import { onlineProductActionRepository } from '../../products/repositories/productActions/onlineProductActions.repository';
import {
  ProductActionsResponseData,
  ProductActionsSearchResponseData,
  SearchProductParams,
} from '../../products/repositories/types/productActions.types';
import { offlineKitsRepository } from '../repositories/offlineKits.repository';
import { offlineOrdersRepository } from '../repositories/offlineOrders.repository';
import { offlineProductColorsRepository } from '../repositories/offlineProductColors.repository';
import { offlineProductsRepository } from '../repositories/offlineProducts.repository';
import { useOfflineOrdersStore } from '../stores/offlineOrders/offlineOrders.store';
import { createApiSauceResponse } from '../utils/createApiSauceResponse.util';
import { createCustomerUnauthorizedProductError } from './utils/createCustomerUnauthorizedError.utils';
import { notImplementedInOfflineError } from './utils/repository.offlineAdapters.utils';
import ShopProductUtils from './utils/shopProduct.utils';
import { ValidateCatalogSectionItemForCustomerCatalogsUtils } from './utils/validateCatalogSectionItemForCustomerCatalogs.utils';

const productActionsRepositoryOfflineAdapters: typeof onlineProductActionRepository = {
  shopByProduct: () => notImplementedInOfflineError('shopByProduct'),

  shopByIncrement: () => notImplementedInOfflineError('shopByIncrement'),

  searchProduct: async ({
    codeOrBarcodeOrName,
  }: SearchProductParams): Promise<TeceoApiResponse<ProductActionsSearchResponseData>> => {
    const kit = await offlineKitsRepository.findByExactCode(codeOrBarcodeOrName);

    if (kit) {
      const response: TeceoApiResponse<ProductActionsSearchResponseData> = {
        ok: true,
        originalError: null,
        problem: null,
        data: {
          kitId: kit.id,
          kitCode: kit.code,
          kitName: kit.name,
        },
      };

      return response;
    }

    const product = await offlineProductsRepository.getByExactCode(codeOrBarcodeOrName);

    if (!product) {
      const response: TeceoApiResponse<ProductActionsResponseData> = {
        ok: false,
        data: {
          code: LangEnum.API_EXCEPTION_PRODUCT_NOT_FOUND,
          message: 'product not found',
          status: HTTP_STATUS.NOT_FOUND,
        },
      };

      return response;
    }

    const productColors = await offlineProductColorsRepository.findByProductId(product.id);

    const response: TeceoApiResponse<ProductActionsResponseData> = {
      ok: true,
      originalError: null,
      problem: null,
      data: {
        isShopping: false,
        productCode: product.code,
        productColorId: productColors[0]?.id,
        productId: product.id,
        productName: product.name,
      },
    };

    return response;
  },

  shopProduct: async ({ productColorId, orderId }) => {
    const brandGeneralSettings = useGeneralSettingsStore.getState().generalSettings;
    const restrictProductsUsingCustomerCatalogs =
      !!brandGeneralSettings.restrictProductsUsingCustomerCatalogs;
    const order = await offlineOrdersRepository.getByIdOrThrow(orderId);
    const customerHasAccessToProductColor =
      await ValidateCatalogSectionItemForCustomerCatalogsUtils.validateForProductColor({
        customerId: order.customer?.id!,
        productColorId,
        restrictProductsUsingCustomerCatalogs,
      });

    if (!customerHasAccessToProductColor) {
      return createCustomerUnauthorizedProductError();
    }

    const gridItem = await ShopProductUtils.getGridItemToAddProduct(productColorId);

    await useOfflineOrdersStore
      .getState()
      .addProductBasedOnGrid({ orderId, gridItems: [gridItem] });

    return createApiSauceResponse<ProductActionsResponseData, ErrorResponse>({
      ok: true,
      data: {
        isShopping: true,
        productColorId,
        productId: '',
        productCode: '',
        productName: '',
      },
    });
  },

  unshopProduct: async ({ orderId, productColorId }) => {
    await useOfflineOrdersStore
      .getState()
      .removeAllOrderProductsFromProductColor(orderId, productColorId);

    return createApiSauceResponse<ProductActionsResponseData, ErrorResponse>({
      ok: true,
      data: {
        isShopping: false,
        productColorId,
        productId: '',
        productCode: '',
        productName: '',
      },
    });
  },
};

export default productActionsRepositoryOfflineAdapters;
